import { Dispatch, SetStateAction } from "react";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  OutlinedInput,
  TextField,
  Typography,
} from "@mui/material";
import { AnswerListPossibility } from "@WahooFitness/cloud-client-types";
import DurationInput from "@/components/MoveToRedesignr/DurationInput";
import useMultiNumericEntry from "./useMultiNumericEntry";
import { Responses } from "@/components/Quiz/useQuiz";

interface Props {
  initialVal?: Array<number | null | string>;
  options: Array<AnswerListPossibility>;
  measurement?: string;
  skipText?: string;
  setCurrentStepHasValue: (val: boolean) => void;
  setCurrentStepValid: (val: boolean) => void;
  setResponses: Dispatch<SetStateAction<Responses | undefined>>;
  questionId: number;
}

function MultiNumericEntry({
  initialVal,
  options,
  measurement,
  skipText,
  setCurrentStepHasValue,
  setCurrentStepValid,
  setResponses,
  questionId,
}: Props) {
  const { values, fieldErrors, handleInputChange, handleBlur, skipChecked, handleSkipToggle } =
    useMultiNumericEntry({
      initialVal,
      options,
      measurement,
      setCurrentStepHasValue,
      setCurrentStepValid,
      setResponses,
      questionId,
    });

  const isDuration = measurement === "race_time";

  return (
    <Box display="flex" flexDirection="column" width="100%" gap={3}>
      {options.map((option) => (
        <Box key={option.key} display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="ui-md-medium">{option.text.title}</Typography>
          {isDuration ? (
            <Box sx={{ position: "relative" }}>
              <FormControl error={!!fieldErrors[option.key]}>
                <OutlinedInput
                  name={option.key}
                  size="small"
                  disabled={skipChecked}
                  value={values[option.key]}
                  onChange={handleInputChange}
                  onBlur={() => handleBlur(option.key)}
                  error={!!fieldErrors[option.key]}
                  inputProps={{ inputMode: "numeric" }}
                  inputComponent={DurationInput}
                  sx={{ width: 140 }}
                  placeholder={option.placeholder ?? undefined}
                />
              </FormControl>
              {fieldErrors[option.key] && (
                <FormHelperText
                  error
                  sx={{
                    position: "absolute",
                    top: "100%",
                    right: 0,
                    m: 0,
                    pr: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  {fieldErrors[option.key]}
                </FormHelperText>
              )}
            </Box>
          ) : (
            <Box sx={{ position: "relative" }}>
              <FormControl error={!!fieldErrors[option.key]}>
                <TextField
                  name={option.key}
                  size="small"
                  disabled={skipChecked}
                  value={values[option.key] ?? ""}
                  onChange={(ev) => {
                    const rawValue = ev.target.value;
                    let parsedValue: number | null = null;
                    if (rawValue !== "") {
                      const numericValue = Number(rawValue);
                      parsedValue = Number.isFinite(numericValue) ? numericValue : null;
                    }
                    handleInputChange({
                      target: {
                        name: option.key,
                        value: parsedValue,
                      },
                    });
                  }}
                  onBlur={() => handleBlur(option.key)}
                  error={!!fieldErrors[option.key]}
                  slotProps={{ htmlInput: { inputMode: "numeric" } }}
                  sx={{ width: 140 }}
                />
              </FormControl>
              {fieldErrors[option.key] && (
                <FormHelperText
                  error
                  sx={{
                    position: "absolute",
                    top: "100%",
                    right: 0,
                    m: 0,
                    pr: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  {fieldErrors[option.key]}
                </FormHelperText>
              )}
            </Box>
          )}
        </Box>
      ))}
      {skipText && (
        <Box mx={2}>
          <FormControlLabel
            control={<Checkbox checked={skipChecked} onChange={handleSkipToggle} />}
            label={skipText}
          />
        </Box>
      )}
    </Box>
  );
}

export default MultiNumericEntry;
